import React, { createContext, useContext, useMemo } from 'react';
import { mergeRichTextSwatches } from '../utils/proposalRichTextPalette';
import {
  buildRichTextFontFamilyOptions,
  buildRichTextFontSizeOptions,
  DEFAULT_RICH_TEXT_FONT_SIZES_REM,
} from '../utils/proposalRichTextFonts';

/**
 * @typedef {'template' | 'proposal'} PuckRichTextEditorMode
 * @typedef {{
 *   mode: PuckRichTextEditorMode,
 *   swatches: { label: string, value: string }[],
 *   proposalStyles: object | null,
 *   fontFamilies: { label: string, value: string }[],
 *   fontSizes: { label: string, value: string }[],
 *   addTemplatePaletteColor?: (hex: string) => void,
 *   canAddPaletteColors: boolean,
 *   allowFreeColors: boolean,
 * }} PuckRichTextStyleContextValue
 */

const PuckRichTextStyleContext = createContext(null);

const DEFAULT_SWATCHES = mergeRichTextSwatches({
  proposalPalette: [],
  templateCustomColors: [],
});
const DEFAULT_CONTEXT_VALUE = {
  mode: 'proposal',
  swatches: DEFAULT_SWATCHES,
  proposalStyles: null,
  fontFamilies: buildRichTextFontFamilyOptions(),
  fontSizes: buildRichTextFontSizeOptions(DEFAULT_RICH_TEXT_FONT_SIZES_REM),
  addTemplatePaletteColor: undefined,
  canAddPaletteColors: false,
  allowFreeColors: true,
};

/**
 * @param {{
 *   mode: PuckRichTextEditorMode,
 *   proposalPalette?: { label: string, value: string }[],
 *   proposalStyles?: object,
 *   templateCustomColors?: { label: string, value: string }[],
 *   addTemplatePaletteColor?: (hex: string) => void,
 *   allowFreeColors?: boolean,
 *   children: React.ReactNode,
 * }} props
 */
export function PuckRichTextStyleProvider({
  mode,
  proposalPalette = [],
  proposalStyles = null,
  templateCustomColors = [],
  addTemplatePaletteColor,
  allowFreeColors = true,
  children,
}) {
  const value = useMemo(() => {
    const isProposalEditor = mode === 'proposal';
    const activeProposalPalette = isProposalEditor ? proposalPalette : [];
    const swatches = mergeRichTextSwatches({
      proposalPalette: activeProposalPalette,
      templateCustomColors: templateCustomColors || [],
      /* Les neutres de secours ne servent que faute de toute palette : sinon
         le nuancier proposait des gris que personne n'avait ajoutés. */
      includeDefaults: activeProposalPalette.length === 0 && !(templateCustomColors || []).length,
    });
    const canAdd = mode === 'template' && typeof addTemplatePaletteColor === 'function';
    return {
      mode,
      swatches,
      /* Le fond des titres vient de la configuration du proposal, pas des props
         du bloc : la sidebar en a besoin pour montrer la couleur réellement
         rendue (cf. `--proposal-hN-bg-color`). */
      proposalStyles,
      fontFamilies: buildRichTextFontFamilyOptions(proposalStyles),
      fontSizes: buildRichTextFontSizeOptions(DEFAULT_RICH_TEXT_FONT_SIZES_REM),
      canAddPaletteColors: canAdd,
      addTemplatePaletteColor: canAdd ? addTemplatePaletteColor : undefined,
      allowFreeColors,
    };
  }, [mode, proposalPalette, proposalStyles, templateCustomColors, addTemplatePaletteColor, allowFreeColors]);

  return (
    <PuckRichTextStyleContext.Provider value={value}>
      {children}
    </PuckRichTextStyleContext.Provider>
  );
}

/** @returns {PuckRichTextStyleContextValue} */
export function usePuckRichTextStyle() {
  const ctx = useContext(PuckRichTextStyleContext);
  return ctx ?? DEFAULT_CONTEXT_VALUE;
}
